import React, { useCallback, useEffect, useRef } from "react";
import { Menu, MenuItem } from "@gluestack-ui/themed";
import {
  Platform,
  TouchableOpacity,
  Modal,
  TouchableWithoutFeedback,
  View,
  Pressable,
  ScrollView,
} from "react-native";
import styled from "styled-components/native";
import { shareMenuOptions } from "../GalleryView/utils/types";
import { useTheme } from "styled-components/native";
import { useTranslation } from "react-i18next";
import { VDBIcons } from "@utils/VDBIcons";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import useFavoriteState from "../../../hooks/useFavoriteState";
import { IconState } from "../IconUtils";
import { ShareVia, useNativeShare } from "../Share/utils/utils";
import { shareMenuTypes } from "./types";
import LineViewContainer from "@scenes/requests/utils/LineView";
import { VDBText } from "@components/atoms";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { useDispatch } from "react-redux";
import { setLoginModalType } from "@redux/slices/appStateSlice";
import {
  filterDataByPriceMode,
  getBoxShadow,
} from "@utils/product/productValidators";
import { useShareOptionConfig } from "@redux/slices/globalSlice";
import { useOrgSettingsSlice } from "@redux/slices/orgSettingsSlice";
import { SpaceType } from "@components/atoms/Spacer/types";
import { SearchResultsAndFavoritesEnum } from "@utils/product/searchResultConstants";
import Routes from "@navigations/Routes";
import { useScreenWiseShareOptionConfig } from "@redux/slices/unifiedProductPages";

const ShareMenu = ({
  setSelectedOption,
  setshowShareModal,
  addExtraPadding,
  itemDetail,
  mediaUrl,
  useRNModal = false,
  isModal = false,
  onShareViaNative,
  productType,
}: shareMenuTypes) => {
  const mediaUrlRef = useRef(mediaUrl);
  const theme = useTheme();
  const userInfo = useUserInfo();
  const { t } = useTranslation();
  const { isVDBOrg } = useOrgSettingsSlice();
  const getScreenWiseShareOptionConfig = useScreenWiseShareOptionConfig(
    productType,
    Routes.PDPScreen
  );
  const screenWiseShareOptionConfig = filterDataByPriceMode(
    Object.values(getScreenWiseShareOptionConfig || {}),
    userInfo.getPriceMode()
  );
  const getShareOptionConfig = filterDataByPriceMode(
    Object.values(useShareOptionConfig()),
    userInfo.getPriceMode()
  );
  const shareOptions =
    screenWiseShareOptionConfig?.length > 0
      ? screenWiseShareOptionConfig
      : getShareOptionConfig?.length > 0
      ? getShareOptionConfig
      : shareMenuOptions(t, isVDBOrg);
  const typography = useTypographyStore();
  const isGuest = userInfo.isGuest;
  const dispatch = useDispatch();
  const {
    setFavoriteIconState,
    setFavoriteMenuOpen,
    handleFavoritesPressIn,
    handleFavoritesPressOut,
    handleFavoritesPress,
    getFavoriteIconColor,
    favoriteMenuOpen,
  } = useFavoriteState();
  const { handleShareViaNative } = useNativeShare(itemDetail);
  const handleSelectionChange = useCallback(
    (key: SearchResultsAndFavoritesEnum) => {
      setTimeout(() => {
        menuOptionSelected(key);
      }, 100);
    },
    []
  );

  const closeMenu = useCallback(() => {
    setFavoriteMenuOpen(false);
    setFavoriteIconState(IconState.Unselected);
  }, [setFavoriteMenuOpen, setFavoriteIconState]);

  useEffect(() => {
    mediaUrlRef.current = mediaUrl;
  }, [mediaUrl]);

  const menuOptionSelected = (eventName: SearchResultsAndFavoritesEnum) => {
    if (isGuest) {
      dispatch(setLoginModalType("default"));
    }
    switch (eventName) {
      case SearchResultsAndFavoritesEnum.SHARE_EMAIL:
        setSelectedOption(ShareVia.EMAIL);
        setshowShareModal(true);
        break;
      case SearchResultsAndFavoritesEnum.SHARE_NATIVE:
        setSelectedOption(ShareVia.NATIVE);
        setshowShareModal(true);
        break;
      case SearchResultsAndFavoritesEnum.SHARE_MEDIA:
        if (onShareViaNative && mediaUrlRef.current && Platform.OS !== "web") {
          onShareViaNative(mediaUrlRef.current);
        } else {
          handleShareViaNative(mediaUrlRef.current);
        }
        break;
      case SearchResultsAndFavoritesEnum.SHARE_VIA_LINK:
        setSelectedOption(ShareVia.LINK);
        setshowShareModal(true);
        break;
    }
  };

  // The portaled popover measures from the window and lands well below the
  // share icon on mobile. An in-tree dropdown stays attached to the icon.
  // iOS modals also cannot present a nested RN Modal, so they use this path.
  const renderInTreeDropdown =
    theme.isMobile &&
    Platform.OS !== "web" &&
    (!isModal || Platform.OS === "ios");

  const handleInTreeTriggerPress = () => {
    if (isGuest) {
      dispatch(setLoginModalType("default"));
      return;
    }
    setFavoriteMenuOpen(!favoriteMenuOpen);
    setFavoriteIconState(
      favoriteMenuOpen ? IconState.Unselected : IconState.Selected
    );
  };

  const handleInTreeOptionSelect = (name: SearchResultsAndFavoritesEnum) => {
    closeMenu();
    menuOptionSelected(name);
  };

  if (shareOptions?.length === 0) {
    return null;
  }

  const menuContent = (
    <StyledMenu
      mt={Platform.OS === "ios" ? 0 : -30}
      py={0}
      borderRadius={8}
      useRNModal={useRNModal}
      marginLeft={!theme.isMobile ? 0 : 10}
      placement={"bottom"}
      backgroundColor={theme["core-01"]}
      isOpen={!isGuest && favoriteMenuOpen}
      onOpen={() => {
        if (isGuest) dispatch(setLoginModalType("default"));
        else {
          setFavoriteMenuOpen(true);
          setFavoriteIconState(IconState.Selected);
        }
      }}
      onClose={closeMenu}
      selectionMode="single"
      trigger={({ ...triggerProps }) => {
        return (
          <MarginLeftContainer addExtraPadding={addExtraPadding}>
            <TouchableOpacity
              onPressIn={handleFavoritesPressIn}
              onPressOut={handleFavoritesPressOut}
              onPress={handleFavoritesPress}
              {...triggerProps}
            >
              <IconContainer>
                <VDBIcon
                  name={VDBIcons.SHARE}
                  size={24}
                  color={getFavoriteIconColor(theme)}
                  variant={IconsVariant.VDBCustom}
                />
              </IconContainer>
            </TouchableOpacity>
          </MarginLeftContainer>
        );
      }}
    >
      {shareOptions &&
        shareOptions
          .filter((option) => mediaUrl || option.name !== "share_media")
          .map((option, index) => {
            return (
              <MenuItem
                key={option.name}
                textValue={option.label}
                style={{ padding: 0, backgroundColor: theme.transparent }}
                onPress={() => handleSelectionChange(option.name)}
              >
                <MenuItemContainer>
                  <MenuItemStyle optionSize={shareOptions.length} index={index}>
                    <VDBText
                      color={theme["core-05"]}
                      type={typography["Minor-M"]}
                    >
                      {Platform.OS === "web" && option.webLabel
                        ? t(option.webLabel, t(option.label))
                        : t(option.label)}
                    </VDBText>
                  </MenuItemStyle>
                  {index < shareOptions.length - 1 && (
                    <LineViewContainer background={"core-03"} />
                  )}
                </MenuItemContainer>
              </MenuItem>
            );
          })}
    </StyledMenu>
  );

  if (renderInTreeDropdown) {
    return (
      <View style={{ position: "relative" }}>
        <MarginLeftContainer addExtraPadding={addExtraPadding}>
          <TouchableOpacity
            onPress={handleInTreeTriggerPress}
            activeOpacity={0.8}
          >
            <IconContainer>
              <VDBIcon
                name={VDBIcons.SHARE}
                size={24}
                color={getFavoriteIconColor(theme)}
                variant={IconsVariant.VDBCustom}
              />
            </IconContainer>
          </TouchableOpacity>
        </MarginLeftContainer>

        {!isGuest && favoriteMenuOpen && (
          <>
            <Pressable
              style={{
                position: "absolute",
                top: -1000,
                left: -1000,
                right: -1000,
                bottom: -1000,
                zIndex: 998,
              }}
              onPress={closeMenu}
            />
            <DropdownShadow
              style={{
                position: "absolute",
                top: 40,
                right: addExtraPadding ? -28 : -16,
                width: 250,
                maxHeight: 300,
                zIndex: 999,
                borderRadius: 8,
                backgroundColor: theme["core-01"],
                shadowColor: "#000",
                shadowOffset: { width: 0, height: 4 },
                shadowOpacity: 0.22,
                shadowRadius: 8,
                elevation: 8,
              }}
            >
              <DropdownContainer
                style={{
                  borderRadius: 8,
                  borderWidth: 1,
                  borderColor: theme["core-03"],
                  backgroundColor: theme["core-01"],
                  maxHeight: 300,
                }}
              >
                <ScrollView
                  showsVerticalScrollIndicator={false}
                  bounces={false}
                >
                  {shareOptions &&
                    shareOptions
                      .filter(
                        (option) => mediaUrl || option.name !== "share_media"
                      )
                      .map((option, index) => (
                        <TouchableOpacity
                          key={option.name}
                          onPress={() => handleInTreeOptionSelect(option.name)}
                          activeOpacity={0.7}
                        >
                          <MenuItemContainer>
                            <MenuItemStyle
                              optionSize={shareOptions.length}
                              index={index}
                            >
                              <VDBText
                                color={theme["core-05"]}
                                type={typography["Minor-M"]}
                              >
                                {t(option.label)}
                              </VDBText>
                            </MenuItemStyle>
                            {index < shareOptions.length - 1 && (
                              <LineViewContainer background={"core-03"} />
                            )}
                          </MenuItemContainer>
                        </TouchableOpacity>
                      ))}
                </ScrollView>
              </DropdownContainer>
            </DropdownShadow>
          </>
        )}
      </View>
    );
  }

  if (isModal && useRNModal && !isGuest && favoriteMenuOpen) {
    return (
      <>
        <Modal transparent visible animationType="none">
          <TouchableWithoutFeedback onPress={closeMenu}>
            <ModalBackdrop />
          </TouchableWithoutFeedback>
        </Modal>
        {menuContent}
      </>
    );
  }

  return <>{menuContent}</>;
};

const StyledMenu = styled(Menu)`
  box-shadow: ${({ theme }) => getBoxShadow(theme.Shadows.Huge)};
`;

const ModalBackdrop = styled.View`
  flex: 1;
  background-color: transparent;
`;

const DropdownShadow = styled.View``;

const DropdownContainer = styled.View`
  overflow: hidden;
`;

const MarginLeftContainer = styled.View<any>`
  margin-left: ${({ addExtraPadding }) => (addExtraPadding ? "12px" : "0px")};
`;

const IconContainer = styled.View<any>`
  padding: ${SpaceType.Basic}px;
`;

const MenuItemStyle = styled.View<any>`
  border-color: #000000;
  width: 250px;
  padding: 10px;
  border-top-left-radius: ${({ optionSize, index }) =>
    optionSize === 1 || index === 0 ? 8 : 0}px;
  border-top-right-radius: ${({ optionSize, index }) =>
    optionSize === 1 || index === 0 ? 8 : 0}px;
  border-bottom-left-radius: ${({ optionSize, index }) =>
    index === optionSize - 1 ? 8 : 0}px;
  border-bottom-right-radius: ${({ optionSize, index }) =>
    index === optionSize - 1 ? 8 : 0}px;
`;

const MenuItemContainer = styled.View``;

export default ShareMenu;
